import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const t = translations[language];

  const scrollToSection = (sectionId) => {
    // If not on homepage, navigate to homepage first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Already on homepage, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <img 
              src="/images/nuzha-logo.jpg" 
              alt="NuzhaTrips Logo" 
              className="w-16 h-16 object-contain"
            />
            <h1 className="text-2xl font-bold text-gray-800">
              Nuzha<span className="text-orange-500">Trips.xyz</span>
            </h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollToSection('home')} className="text-gray-800 hover:text-orange-500 font-medium transition-colors">
              {t.home}
            </button>
            <button onClick={() => { navigate('/premium'); setMobileMenuOpen(false); }} className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-2 rounded-full font-bold hover:shadow-lg transition-all">
              {language === 'en' ? 'Premium ⭐' : 'مميز ⭐'}
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-800 hover:text-orange-500 font-medium transition-colors">
              {t.about}
            </button>
            <button onClick={() => scrollToSection('pricing')} className="text-gray-800 hover:text-orange-500 font-medium transition-colors">
              {t.pricing}
            </button>
            <button onClick={() => { navigate('/reviews'); setMobileMenuOpen(false); }} className="text-gray-800 hover:text-orange-500 font-medium transition-colors">
              {language === 'en' ? 'Reviews ⭐' : 'التقييمات ⭐'}
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-800 hover:text-orange-500 font-medium transition-colors">
              {t.contact}
            </button>
          </nav>

          {/* Language Toggle & Mobile Menu Button */}
          <div className="flex items-center gap-4">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="relative inline-flex items-center h-8 w-16 rounded-full bg-orange-500 cursor-pointer transition-colors"
            >
              <span className={`block h-6 w-6 rounded-full bg-white shadow-md transform transition-transform duration-300 ${language === 'ar' ? 'translate-x-9' : 'translate-x-1'} flex items-center justify-center text-xs font-bold text-orange-500`}>
                {language === 'ar' ? 'AR' : 'EN'}
              </span>
            </button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 flex flex-col gap-3 border-t pt-4">
            <button onClick={() => scrollToSection('home')} className="text-gray-800 hover:text-orange-500 font-medium py-2 text-left transition-colors">
              {t.home}
            </button>
            <button onClick={() => { navigate('/premium'); setMobileMenuOpen(false); }} className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-2 rounded-full font-bold text-center">
              {language === 'en' ? 'Premium ⭐' : 'مميز ⭐'}
            </button>
            <button onClick={() => scrollToSection('about')} className="text-gray-800 hover:text-orange-500 font-medium py-2 text-left transition-colors">
              {t.about}
            </button>
            <button onClick={() => scrollToSection('pricing')} className="text-gray-800 hover:text-orange-500 font-medium py-2 text-left transition-colors">
              {t.pricing}
            </button>
            <button onClick={() => { navigate('/reviews'); setMobileMenuOpen(false); }} className="text-gray-800 hover:text-orange-500 font-medium py-2 text-left transition-colors">
              {language === 'en' ? 'Reviews ⭐' : 'التقييمات ⭐'}
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-gray-800 hover:text-orange-500 font-medium py-2 text-left transition-colors">
              {t.contact}
            </button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
